if (!customElements.get('almila-home-categories')) {
  class AlmilaHomeCategories extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-category-viewport]');
      this.cards = [...this.querySelectorAll('[data-category-card]')];
      this.dots = [...this.querySelectorAll('[data-category-dot]')];
      this.previous = this.querySelector('[data-category-prev]');
      this.next = this.querySelector('[data-category-next]');
      this.counter = this.querySelector('[data-category-position]');
      if (!this.viewport || this.cards.length < 2) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.show(this.active - 1), options);
      this.next.addEventListener('click', () => this.show(this.active + 1), options);
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.show(index), options);
        dot.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = Math.min(index + 1, this.dots.length - 1);
          if (event.key === 'ArrowLeft') target = Math.max(index - 1, 0);
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.dots.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.dots[target].focus();
          this.show(target);
        }, options);
      });
      this.viewport.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      }, { ...options, passive: true });
      this.viewport.addEventListener('focusin', event => {
        const index = this.cards.indexOf(event.target.closest('[data-category-card]'));
        if (index < 0) return;
        const card = this.cards[index].getBoundingClientRect();
        const view = this.viewport.getBoundingClientRect();
        if (card.left < view.left + 4 || card.right > view.right - 4) this.show(index, 'auto');
      }, options);
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.viewport);
      this.setAttribute('data-carousel-ready', '');
      this.update();
    }

    position(index) {
      const view = this.viewport.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(this.viewport).paddingLeft) || 0;
      return this.viewport.scrollLeft + this.cards[index].getBoundingClientRect().left - view.left - inset;
    }

    show(index, behavior) {
      if (!this.controller) return;
      const target = Math.max(0, Math.min(index, this.cards.length - 1));
      this.viewport.scrollTo({ left: this.position(target), behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth') });
    }

    update() {
      if (!this.controller) return;
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const left = this.viewport.scrollLeft;
      // Only a scrolling viewport is a keyboard stop; the grid layout is plain links.
      if (maximum > 1) this.viewport.setAttribute('tabindex', '0');
      else this.viewport.removeAttribute('tabindex');
      this.active = this.cards.reduce((nearest, _, index) => Math.abs(this.position(index) - left) < Math.abs(this.position(nearest) - left) ? index : nearest, 0);
      const atStart = left <= 1;
      const atEnd = left >= maximum - 1;
      // A focused arrow that becomes disabled would drop keyboard focus to the page: hand it to the other arrow.
      if (atStart && !atEnd && document.activeElement === this.previous) this.next.focus({ preventScroll: true });
      if (atEnd && !atStart && document.activeElement === this.next) this.previous.focus({ preventScroll: true });
      this.previous.disabled = atStart;
      this.next.disabled = atEnd;
      const position = maximum > 0 && left >= maximum - 1 ? this.cards.length : this.active + 1;
      const count = `${position} / ${this.cards.length}`;
      if (this.counter && this.counter.textContent !== count) this.counter.textContent = count;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.active)));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.removeAttribute('data-carousel-ready');
    }
  }
  customElements.define('almila-home-categories', AlmilaHomeCategories);
  const components = scope => [...(scope.matches?.('almila-home-categories') ? [scope] : []), ...scope.querySelectorAll('almila-home-categories')];
  document.addEventListener('shopify:section:load', event => components(event.target).forEach(element => { element.connectedCallback(); element.update(); }));
  document.addEventListener('shopify:section:unload', event => components(event.target).forEach(element => element.disconnectedCallback()));
  document.addEventListener('shopify:section:reorder', event => components(event.target).forEach(element => element.update()));
  document.addEventListener('shopify:block:select', event => {
    const element = event.target.closest('almila-home-categories');
    if (element) element.show(element.cards.indexOf(event.target.closest('[data-category-card]')), 'auto');
  });
}
